import http from '@/lib/request/svc/hr/http';
import getInstallationId from '@/lib/facades/get-installation-id';

export interface JobTitle {
  id: string;
  name: string;
}

interface RawJobTitle {
  id?: string;
  name?: string;
  archived?: boolean;
}

interface RawJobTitleListData {
  jobTitles?: RawJobTitle[];
  pagination?: { nextCursor?: string };
}

/**
 * svc-hr wraps the payload in `{ success, data, error }` and `http.js` carries
 * only a request interceptor, so nothing unwraps it. Reading `jobTitles` off
 * the response root silently yields an empty library forever.
 */
interface RawJobTitleListEnvelope {
  success?: boolean;
  data?: RawJobTitleListData;
}

/** Backstop so a broken cursor cannot spin forever. */
const MAX_PAGES = 20;

const MOCK_FLAG_KEY = 'job-titles:mock';

const MOCK_LIBRARY: JobTitle[] = [
  'Assistant Venue Manager (2IC)',
  'Bar Attendant',
  'Barista',
  'Cook',
  'Head Chef',
  'Kitchen Hand',
  'Shift Supervisor',
  'Venue Manager',
  'Waitstaff'
].map((name, index) => ({ id: `mock-${index + 1}`, name }));

/**
 * Opt-IN, so the real request is the default path on every environment
 * including tests. Enable with localStorage['job-titles:mock'] = 'true' while
 * no deployed environment serves the endpoint.
 */
function isMockEnabled(): boolean {
  if (process.env.NODE_ENV === 'production') {
    return false;
  }

  try {
    return localStorage.getItem(MOCK_FLAG_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * proto3-JSON omits empty arrays and false booleans, so entries are read by
 * shape rather than truthiness. Archived titles are filtered out: a title
 * cannot be archived while held, so one can never be a live assignment.
 */
export function toJobTitles(data: RawJobTitleListData | undefined): JobTitle[] {
  const raw = Array.isArray(data?.jobTitles) ? data.jobTitles : [];

  return raw
    .filter((entry): entry is RawJobTitle & { id: string } => typeof entry?.id === 'string')
    .filter(entry => entry.archived !== true)
    .map(entry => ({
      id: entry.id,
      name: typeof entry.name === 'string' ? entry.name : ''
    }));
}

/**
 * Walks every page. The select has no lazy loading, so a title sitting past the
 * first page would be unselectable and an existing assignment on a later page
 * would not resolve to a name.
 */
export async function listJobTitles(): Promise<JobTitle[]> {
  if (isMockEnabled()) {
    return MOCK_LIBRARY;
  }

  const tenantId = getInstallationId();
  const titles: JobTitle[] = [];
  let cursor: string | undefined;
  let page = 0;

  do {
    const { data } = await http.get(`/tenants/${tenantId}/job-titles`, {
      params: cursor ? { cursor } : {},
    });

    const payload = (data as RawJobTitleListEnvelope | undefined)?.data;

    titles.push(...toJobTitles(payload));

    const next = payload?.pagination?.nextCursor;
    cursor = typeof next === 'string' && next !== '' ? next : undefined;
    page += 1;
  } while (cursor && page < MAX_PAGES);

  return titles;
}

export function readJobTitlePage(response: unknown): {
  titles: JobTitle[];
  nextCursor: string | undefined;
} {
  const payload = (response as RawJobTitleListEnvelope | undefined)?.data;
  const next = payload?.pagination?.nextCursor;

  return {
    titles: toJobTitles(payload),
    nextCursor: typeof next === 'string' && next !== '' ? next : undefined,
  };
}

export default listJobTitles;
